import { getCompany } from "../../../../shared/data/storage/authStorage";
import { getCurrencySymbol } from "../../../../shared/utils/formatters";

/** 1 Thousand = 10^3 */
const THOUSAND = 1_000;
/** 1 Lakh = 10^5 */
const LAKH = 100_000;
/** 1 Million = 10^6 */
const MILLION = 1_000_000;
/** 1 Crore = 10^7 */
const CRORE = 10_000_000;
/** 1 Billion = 10^9 */
const BILLION = 1_000_000_000;

type AmountCompactUnit =
  | "whole"
  | "thousand"
  | "lakh"
  | "million"
  | "crore"
  | "billion";

type AmountCompactSuffixMap = Partial<Record<AmountCompactUnit, string>>;

function getCompanyAmountFormatConfig(): {
  unit: AmountCompactUnit;
  decimals: 1 | 2;
  suffixMap: AmountCompactSuffixMap;
  currencyCode: string;
  currencySymbol: string;
} {
  const company = getCompany();
  const currencyCode = (company?.currency ?? "INR").toUpperCase();

  const rawUnit = (company?.amountCompactUnit ?? "").toString().toLowerCase();
  const unit: AmountCompactUnit =
    rawUnit === "whole" ||
    rawUnit === "thousand" ||
    rawUnit === "lakh" ||
    rawUnit === "million" ||
    rawUnit === "crore" ||
    rawUnit === "billion"
      ? rawUnit
      : rawUnit === "crores"
        ? "crore"
        : rawUnit === "crors"
          ? "crore"
          : rawUnit === "lakhs"
            ? "lakh"
            : "lakh";

  const decimals: 1 | 2 =
    company?.amountCompactDecimals === 1 || company?.amountCompactDecimals === 2
      ? company.amountCompactDecimals
      : 2;

  const defaultSuffixMap: AmountCompactSuffixMap = {
    whole: "",
    thousand: "k",
    lakh: "L",
    million: "M",
    crore: "Cr",
    billion: "B",
  };

  let suffixMap: AmountCompactSuffixMap = defaultSuffixMap;
  const rawSuffixJson = company?.amountCompactSuffixJson;
  if (typeof rawSuffixJson === "string" && rawSuffixJson.trim()) {
    try {
      const parsed = JSON.parse(rawSuffixJson) as Record<string, unknown>;
      const merged: AmountCompactSuffixMap = { ...defaultSuffixMap };
      for (const k of Object.keys(parsed)) {
        const v = parsed[k];
        if (typeof v !== "string") continue;
        const key = k.toLowerCase();
        if (
          key === "whole" ||
          key === "thousand" ||
          key === "lakh" ||
          key === "million" ||
          key === "crore" ||
          key === "billion"
        ) {
          merged[key] = v;
        }
      }
      suffixMap = merged;
    } catch {
      suffixMap = defaultSuffixMap;
    }
  }

  const currencySymbol = getCurrencySymbol(currencyCode);
  return { unit, decimals, suffixMap, currencyCode, currencySymbol };
}

/** Truncate toward zero to N fractional digits (no rounding up). */
function truncateToDecimals(n: number, decimals: 1 | 2): number {
  if (!Number.isFinite(n)) return n;
  const sign = n < 0 ? -1 : 1;
  const a = Math.abs(n);
  const factor = decimals === 1 ? 10 : 100;
  return sign * (Math.floor(a * factor + Number.EPSILON) / factor);
}

function formatScaled(
  amount: number,
  divisor: number,
  suffix: string,
  currencySymbol: string,
  decimals: 1 | 2,
): string {
  const neg = amount < 0;
  const v = Math.abs(amount) / divisor;
  const s = truncateToDecimals(v, decimals).toFixed(decimals);
  return `${neg ? "-" : ""}${currencySymbol}${s}${suffix}`;
}

function unitDivisor(unit: AmountCompactUnit): number {
  switch (unit) {
    case "whole":
      return 1;
    case "thousand":
      return THOUSAND;
    case "lakh":
      return LAKH;
    case "million":
      return MILLION;
    case "crore":
      return CRORE;
    case "billion":
      return BILLION;
  }
}

/**
 * Format amount for P&L / overview using company config: k + (L or Cr).
 * Truncates to 2 decimal places (does not round up). Used for Month at Glance and similar.
 * If `currencySymbol` is not provided, it uses company `currency` from local storage.
 */
export function formatPlAmount(
  amount: number | null | undefined,
  currencySymbol?: string,
): string {
  if (amount === null || amount === undefined) return "—";
  const {
    unit,
    decimals,
    suffixMap,
    currencySymbol: symFromConfig,
  } = getCompanyAmountFormatConfig();
  const sym = currencySymbol?.trim() || symFromConfig || "₹";

  // For small magnitudes, show the exact number instead of compacting into L/Cr/etc.
  // This avoids values like 0.2 being displayed as ₹0Cr when the configured unit is large.
  const abs = Math.abs(amount);
  const EXACT_THRESHOLD = 10_000;
  if (Number.isFinite(amount) && abs > 0 && abs < EXACT_THRESHOLD) {
    const truncated = truncateToDecimals(abs, 2);
    const frac = truncated % 1;
    const minFractionDigits = frac === 0 ? 0 : 1;
    const s = truncated.toLocaleString("en-IN", {
      minimumFractionDigits: minFractionDigits,
      maximumFractionDigits: 2,
    });
    return `${amount < 0 ? "-" : ""}${sym}${s}`;
  }

  // Always convert to the company-selected unit (do not auto-switch units by magnitude).
  // Example: unit="crore" => show ₹0.01Cr for small amounts rather than ₹10.00k/₹1000.00.
  const divisor = unitDivisor(unit);
  const suffix = suffixMap[unit] ?? "";
  return formatScaled(amount, divisor, suffix, sym, decimals);
}

/**
 * Format amount with Indian comma system (e.g. 1,00,000 for 1 Lakh).
 * Use on Cash & Bank page for all numeric display.
 */
export function formatIndianAmount(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) return "—";
  if (!Number.isFinite(amount)) return "—";
  const { currencySymbol } = getCompanyAmountFormatConfig();
  const abs = Math.abs(amount);
  const s = abs.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${amount < 0 ? "-" : ""}${currencySymbol}${s}`;
}

/**
 * Format percentage for change (e.g. +3.8%, -2.1%).
 */
export function formatPlChangePercent(
  changePercent: number | null | undefined,
): string {
  if (changePercent === null || changePercent === undefined) return "—";
  const sign = changePercent >= 0 ? "+" : "";
  return `${sign}${changePercent}%`;
}

const CHART_BAR_HEIGHT = 224; // h-56 = 14rem = 224px

/**
 * Round up to a "nice" step for chart Y-axis (amounts). Exported for scaling bars to same max.
 */
export function niceMaxAmount(value: number): number {
  if (value <= 0) return 100_000;
  const order = Math.pow(10, Math.floor(Math.log10(value)));
  const normalized = value / order;
  if (normalized <= 1) return order;
  if (normalized <= 2) return 2 * order;
  if (normalized <= 5) return 5 * order;
  return 10 * order;
}

/**
 * Nice lower bound for negative chart domains (≤ value).
 */
export function niceMinAmount(value: number): number {
  if (value >= 0) return 0;
  return -niceMaxAmount(Math.abs(value));
}

/**
 * Y-axis min/max including zero so bars can extend above/below a baseline.
 */
export function chartDomainWithZero(values: readonly number[]): {
  yMin: number;
  yMax: number;
} {
  if (!values.length) {
    return { yMin: 0, yMax: niceMaxAmount(100_000) };
  }
  const rawMin = Math.min(...values);
  const rawMax = Math.max(...values);
  const extentMin = Math.min(0, rawMin);
  const extentMax = Math.max(0, rawMax);
  if (extentMin === 0 && extentMax === 0) {
    return { yMin: 0, yMax: niceMaxAmount(100_000) };
  }
  const yMin = extentMin < 0 ? niceMinAmount(extentMin) : 0;
  const yMax = extentMax > 0 ? niceMaxAmount(extentMax) : 0;
  const span = yMax - yMin;
  if (span <= 0) {
    return { yMin: yMin - 1, yMax: yMax + 1 };
  }
  return { yMin, yMax };
}

/** Map amount to distance from bottom of plot (0 = bottom edge = yMin). */
export function amountToPlotBottomPx(
  value: number,
  yMin: number,
  yMax: number,
  plotHeightPx: number,
): number {
  const span = Math.max(yMax - yMin, 1);
  return ((value - yMin) / span) * plotHeightPx;
}

export interface TrendBarLayout {
  bottomPx: number;
  heightPx: number;
  /** Rounded end away from zero (top for positive, bottom for negative). */
  roundedEnd: "top" | "bottom";
}

export function layoutTrendBarFromZero(
  value: number,
  yMin: number,
  yMax: number,
  plotHeightPx: number,
  minBarPx = 2,
): TrendBarLayout {
  const zeroPx = amountToPlotBottomPx(0, yMin, yMax, plotHeightPx);
  const valPx = amountToPlotBottomPx(value, yMin, yMax, plotHeightPx);
  if (value >= 0) {
    return {
      bottomPx: zeroPx,
      heightPx: Math.max(valPx - zeroPx, minBarPx),
      roundedEnd: "top",
    };
  }
  return {
    bottomPx: valPx,
    heightPx: Math.max(zeroPx - valPx, minBarPx),
    roundedEnd: "bottom",
  };
}

/**
 * Generate Y-axis labels from min to max (inclusive). Top to bottom = high to low.
 */
export function chartYAxisLabelsForRange(
  minAmount: number,
  maxAmount: number,
  tickCount = 5,
): string[] {
  const span = maxAmount - minAmount;
  if (tickCount < 2 || span <= 0) {
    return [formatPlAmount(maxAmount), formatPlAmount(minAmount)];
  }
  const labels: string[] = [];
  for (let i = tickCount - 1; i >= 0; i--) {
    const t = i / (tickCount - 1);
    labels.push(formatPlAmount(minAmount + t * span));
  }
  return labels;
}

/**
 * Generate Y-axis labels for amount charts (k, L). Top to bottom.
 */
export function chartYAxisLabelsForAmount(
  maxAmount: number,
  tickCount = 5,
): string[] {
  const max = niceMaxAmount(maxAmount);
  const step = max / (tickCount - 1);
  const labels: string[] = [];
  for (let i = tickCount - 1; i >= 0; i--) {
    labels.push(formatPlAmount(i * step));
  }
  return labels;
}

/**
 * Generate Y-axis labels with Indian comma format for Cash & Bank charts.
 */
export function chartYAxisLabelsIndian(
  maxAmount: number,
  tickCount = 5,
): string[] {
  const max = niceMaxAmount(maxAmount);
  const step = max / (tickCount - 1);
  const labels: string[] = [];
  for (let i = tickCount - 1; i >= 0; i--) {
    labels.push(formatIndianAmount(i * step));
  }
  return labels;
}

/**
 * Scale a value to bar height (0..CHART_BAR_HEIGHT) given max value.
 */
export function scaleToBarHeight(value: number, maxValue: number): number {
  if (maxValue <= 0) return 0;
  const ratio = Math.min(value / maxValue, 1);
  return Math.round(ratio * CHART_BAR_HEIGHT);
}

/**
 * Scale a percentage (0–100) to position from bottom for EBITDA chart (0–100).
 */
export function scalePercentToPosition(pct: number, yMax: number): number {
  if (yMax <= 0) return 0;
  return Math.min((pct / yMax) * 100, 100);
}
